<template>
    <div class="container relative z-2">
        <div class="swiper-container">
            <div ref="swiper" class="swiper overflow-visible mx-[-10px] px-[10px] sm:mx-0 sm:px-0">
                <div
                    class="swiper-wrapper overflow-visible lg:mx-0 lg:px-0 lg:grid lg:grid-cols-12 lg:gap-x-7 lg:gap-y-7"
                >
                    <div
                        v-for="(item, i) in items"
                        :key="`n${i}`"
                        class="swiper-slide"
                        :class="getCardOptions(i).wrapperClass"
                    >
                        <PlaceCard
                            v-if="!partialInitialDisplay || i < initialDisplayCount"
                            :slug="item.slug"
                            :title="item.name"
                            :img-src-set="item.media.lead"
                            :orientation="getCardOptions(i).orientation"
                            :rounding="getCardOptions(i).rounding"
                            :class="getCardOptions(i).cardClass"
                            :narrow="getCardOptions(i).narrow"
                        />

                        <Transition v-else name="fade" :css="false" @enter="onEnter">
                            <PlaceCard
                                v-show="showAll"
                                :slug="item.slug"
                                :title="item.name"
                                :img-src-set="item.media.lead"
                                :orientation="getCardOptions(i).orientation"
                                :rounding="getCardOptions(i).rounding"
                                :class="getCardOptions(i).cardClass"
                                :narrow="getCardOptions(i).narrow"
                                :data-index="i"
                                style="opacity: 0"
                            />
                        </Transition>
                    </div>
                </div>

                <div class="carousel-pagination-block container relative mt-12 lg:hidden">
                    <div class="flex items-center justify-center sm:justify-end">
                        <button ref="prevBtn" class="swiper-button-prev"></button>
                        <span class="gallery-pagination-counter">01</span>
                        <div ref="pagination" class="swiper-pagination"></div>
                        <span class="gallery-pagination-counter">{{ String(items.length).padStart(2, '0') }}</span>
                        <button ref="nextBtn" class="swiper-button-next"></button>
                    </div>
                </div>
            </div>
        </div>

        <div v-if="hasHiddenItems && !showAll" class="text-center hidden lg:block">
            <Button
                class="p-button-secondary p-button-outlined"
                :label="$t('generic.see_all')"
                @click.native="showAllPlaceCards()"
            />
        </div>
    </div>
</template>

<script>
import { gsap } from 'gsap/dist/gsap';
import PlaceCard from '@/components/Cards/PlaceCard/PlaceCard.vue';

export default {
    name: 'PlaceGridBlock',
    components: { PlaceCard },
    props: {
        items: {
            type: Array,
            required: true,
        },
        partialInitialDisplay: {
            type: Boolean,
            required: false,
            default: false,
        },
    },
    data() {
        return {
            initialDisplayCount: 6,
            showAll: false,
            carousel: null,
        };
    },
    computed: {
        hasHiddenItems() {
            return this.partialInitialDisplay && this.items.length > this.initialDisplayCount;
        },
    },
    watch: {
        '$store.state.windowWidth'(value) {
            if (value < 1024 && !this.carousel) {
                this.createCarousel();
            } else if (value >= 1024 && this.carousel) {
                this.destroyCarousels();
            }
        },
    },
    mounted() {
        if (window.innerWidth < 1024) {
            this.createCarousel();
        }
    },
    methods: {
        getCardOptions(i) {
            const value = {
                orientation: undefined,
                rounding: undefined,
                narrow: false,
                wrapperClass: '',
                cardClass: '',
            };

            if (i === 12 || (i > 0 && i % 25 === 0)) {
                value.rounding = 'medium';
                value.cardClass = 'lg:-mt-8';
                value.wrapperClass = 'col-span-3 lg:pt-8 lg:pl-12';
            } else if (i === 11 || (i > 0 && i % 24 === 0)) {
                value.orientation = 'landscape';
                value.rounding = 'full';
                value.narrow = true;
                value.wrapperClass = 'col-span-5 lg:mt-4';
            } else if (i === 10 || (i > 0 && i % 23 === 0)) {
                value.orientation = 'portrait';
                value.rounding = 'full';
                value.wrapperClass = 'col-span-4 lg:px-12 lg:mt-14 row-span-2';
            } else if (i === 9 || (i > 0 && i % 22 === 0)) {
                value.orientation = 'landscape';
                value.wrapperClass = 'col-span-4 lg:pl-4 lg:pr-12';
            } else if (i === 8 || (i > 0 && i % 21 === 0)) {
                value.orientation = 'landscape';
                value.rounding = 'medium';
                value.cardClass = 'lg:-mt-12';
                value.wrapperClass = 'col-span-4';
            } else if (i === 7 || (i > 0 && i % 20 === 0)) {
                value.orientation = 'portrait';
                value.wrapperClass = 'col-span-4 lg:px-12 lg:mt-4 row-span-2';
            } else if (i === 6 || (i > 0 && i % 19 === 0)) {
                value.rounding = 'full';
                value.cardClass = 'lg:-mt-44';
                value.wrapperClass = 'col-span-4 lg:pr-16';
            } else if (i === 5 || (i > 0 && i % 18 === 0)) {
                value.orientation = 'landscape';
                value.rounding = 'medium';
                value.cardClass = 'lg:-mr-8';
                value.narrow = true;
                value.wrapperClass = 'col-span-5 col-start-4 lg:pl-12 lg:pr-4 lg:pt-8';
            } else if (i === 4 || (i > 0 && i % 17 === 0)) {
                value.orientation = 'portrait';
                value.rounding = 'full';
                value.cardClass = 'lg:mt-16 lg:-mb-16';
                value.wrapperClass = 'col-span-4 lg:px-12 lg:mt-12 row-span-2';
            } else if (i === 3 || (i > 0 && i % 16 === 0)) {
                value.rounding = 'full';
                value.cardClass = 'lg:-mt-20';
                value.wrapperClass = 'col-span-4 lg:px-4';
            } else if (i === 2 || (i > 0 && i % 15 === 0)) {
                value.cardClass = 'lg:mt-12 lg:-mb-12';
                value.wrapperClass = 'col-span-3 col-start-2 lg:-translate-x-12';
            } else if (i === 1 || (i > 0 && i % 14 === 0)) {
                value.orientation = 'landscape';
                value.rounding = 'full';
                value.wrapperClass = 'col-span-5 col-start-8';
            } else if (i === 0 || (i > 0 && i % 13 === 0)) {
                value.orientation = 'landscape';
                value.wrapperClass = 'col-span-5';
            }

            return value;
        },

        onEnter(el, done) {
            gsap.to(el, {
                opacity: 1,
                delay: el.dataset.index * 0.05,
                onComplete: done,
            });
        },
        createCarousel() {
            this.carousel = new this.$swiper(this.$refs.swiper, {
                spaceBetween: 16,
                slidesPerView: 1.2,
                centeredSlides: false,
                loop: false,
                navigation: {
                    nextEl: this.$refs.nextBtn,
                    prevEl: this.$refs.prevBtn,
                },
                pagination: {
                    el: this.$refs.pagination,
                    clickable: true,
                    bulletElement: 'button',
                },
                breakpoints: {
                    640: {
                        spaceBetween: 16,
                        slidesPerView: 2.2,
                    },
                    1024: {
                        a11y: {
                            enabled: false,
                        },
                    },
                },
                a11y: {
                    containerMessage: this.$t('carousel.a11y.container_message_project'),
                    paginationBulletMessage: this.$t('carousel.a11y.pagination_bullet_message'),
                    firstSlideMessage: this.$t('carousel.a11y.first_slide_message'),
                    lastSlideMessage: this.$t('carousel.a11y.last_slide_message'),
                    nextSlideMessage: this.$t('carousel.a11y.next_slide_message'),
                    prevSlideMessage: this.$t('carousel.a11y.prev_slide_message'),
                },
            });
        },
        destroyCarousels() {
            this.carousel.destroy();
        },
        showAllPlaceCards() {
            this.showAll = true;
        },
    },
};
</script>

<style scoped>
@media (max-width: 1023px) {
    .swiper-slide > * {
        display: block !important;
        opacity: 1 !important;
    }
}
@media (min-width: 1024px) {
    .swiper {
        overflow: visible;
    }
    .swiper-slide {
        width: unset !important;
    }
}
</style>
